import { Check, Close } from "@mui/icons-material";
import { FC, RefObject } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IRideDto, onActivateKeyDown, useIsMobile } from "@ryde/shared";

import { DropdownTemplate, SwitchTemplate } from "../../../../dynamic-forms";
import { ButtonAddStation, ButtonOpenStation } from "../controls";
import { useRideStationBulkActions } from "./ride-station-bulk-actions.hook";
import { RideBulkActionFields } from "./ride-station-bulk-actions.models";
import { RideStationBulkBackButton } from "./ride-station-bulk-back-button";
import { BulkActionFormStyled } from "./ride-stations.styled";

interface IRideStationBulkActionsFormProps {
  rideDto: Partial<IRideDto>;
  backButtonRef: RefObject<HTMLButtonElement>;
  onBack: () => void;
}

export const RideStationBulkActionsForm: FC<
  IRideStationBulkActionsFormProps
> = ({ rideDto, backButtonRef, onBack }) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const {
    formMethods,
    stationsInDropdownList,
    stationsOutDropdownList,
    addStationFromHandler,
    addStationToHandler,
    stationFromWatchedId,
    stationToWatchedId,
    openStationFromCard,
    openStationToCard,
    bulkActionHandler,
    deselectAllPassengers,
    selectedPassengersIds,
    optionIconMappingFrom,
    optionIconMappingTo,
    boldStationsFromIds,
    boldStationsToIds,
  } = useRideStationBulkActions(rideDto);

  return (
    <BulkActionFormStyled>
      <div className="bulk_panel_header">
        <RideStationBulkBackButton buttonRef={backButtonRef} onClick={onBack} />
        <span className="bulk_panel_title">
          {t("ride.BULK_ACTION_UPDATE_STATIONS")}
        </span>
      </div>

      <FormProvider {...formMethods}>
        <div className="stations_dropdowns_container_bulk">
          <DropdownTemplate
            name={RideBulkActionFields.stationBulkFromId}
            size="small"
            formAttributes={{
              selectItems: stationsInDropdownList,
              boldValues: boldStationsFromIds,
              optionIconMapping: optionIconMappingFrom,
              skipDropdownItemsTranslation: true,
            }}
          />

          <span className="station_buttons_bulk">
            <ButtonAddStation callbackFn={addStationFromHandler} />
            <ButtonOpenStation
              watchedId={stationFromWatchedId}
              onClick={openStationFromCard}
            />
          </span>

          <DropdownTemplate
            name={RideBulkActionFields.stationBulkToId}
            size="small"
            formAttributes={{
              selectItems: stationsOutDropdownList,
              boldValues: boldStationsToIds,
              optionIconMapping: optionIconMappingTo,
              skipDropdownItemsTranslation: true,
            }}
          />
          <span className="station_buttons_bulk">
            <ButtonAddStation callbackFn={addStationToHandler} />
            <ButtonOpenStation
              watchedId={stationToWatchedId}
              onClick={openStationToCard}
            />
          </span>
        </div>
        <div className="checkbox_container_bulk">
          <SwitchTemplate
            size={isMobile ? "small" : "medium"}
            labelPlacement={isMobile ? "start" : "end"}
            className="only_existing_bulk_station_switch"
            name={RideBulkActionFields.onlyExistingBulkStations}
          />

          <span
            className="cancel_button"
            onClick={deselectAllPassengers}
            onKeyDown={onActivateKeyDown(deselectAllPassengers)}
            role="button"
            tabIndex={0}
          >
            {t("general.CANCEL")} <Close />
          </span>
          <span
            className="perform_bulk_action_button"
            onClick={bulkActionHandler}
            onKeyDown={onActivateKeyDown(bulkActionHandler)}
            role="button"
            tabIndex={0}
          >
            {t("ride.BULK_ACTION_UPDATE_SELECTED_PASSENGER", {
              count: selectedPassengersIds?.length || 0,
            })}
            <Check />
          </span>
        </div>
      </FormProvider>
    </BulkActionFormStyled>
  );
};
